import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseClassicApiService } from "@app/core/services/api/classic/base-classic-api.service";
import { LoadingService } from "@app/core/services/loading.service";
import { Observable, throwError, timer } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";

import {
  ForumTocResponse,
  ForumTocData,
  isForumTocSuccessResponse,
  isForumTocBuildingResponse,
  ForumTocEntry,
  ForumTocEditHistory,
  ForumTocEditRequest
} from "../models/forum-toc.interface";

@Injectable({
  providedIn: "root"
})
export class ForumTocService extends BaseClassicApiService {
  private readonly tocUrl = this.baseUrl + "/forum/topic";
  private readonly maxRetries = 10;
  private readonly initialRetryDelay = 2000; // 2 seconds
  private readonly maxRetryDelay = 30000; // 30 seconds

  constructor(
    public loadingService: LoadingService,
    private http: HttpClient
  ) {
    super(loadingService);
  }

  /**
   * Fetches the Table of Contents for a forum topic (v2 unified API)
   * Now includes complete entry data with edit history
   * @param topicId The ID of the forum topic
   * @returns Observable of the ToC data
   */
  getTableOfContents(topicId: number): Observable<ForumTocData> {
    const url = `${this.tocUrl}/${topicId}/toc/`;
    const params = {
      page: "1",
      page_size: "50" // Default page size
    };

    this.loadingSubject.next(true);

    return this.http.get<ForumTocResponse>(url, { params }).pipe(
      switchMap(response => this.handleTocResponse(response, topicId)),
      catchError(error => this.handleError(error)),
      map(response => {
        this.loadingSubject.next(false);
        return response;
      })
    );
  }

  /**
   * Handles the ToC response, implementing retry logic for building state
   */
  private handleTocResponse(response: ForumTocResponse, topicId: number, retryCount = 0): Observable<ForumTocData> {
    if (isForumTocSuccessResponse(response)) {
      return new Observable(observer => {
        observer.next(response);
        observer.complete();
      });
    }

    if (isForumTocBuildingResponse(response)) {
      if (retryCount >= this.maxRetries) {
        return throwError(() => new Error("Table of contents generation timed out"));
      }

      // Calculate exponential backoff delay
      const delay = Math.min(this.initialRetryDelay * Math.pow(1.5, retryCount), this.maxRetryDelay);

      // Retry after delay
      return timer(delay).pipe(
        take(1),
        switchMap(() => {
          const url = `${this.tocUrl}/${topicId}/toc/`;
          const params = {
            page: "1",
            page_size: "50" // Keep same page size for retries
          };
          return this.http
            .get<ForumTocResponse>(url, { params })
            .pipe(switchMap(retryResponse => this.handleTocResponse(retryResponse, topicId, retryCount + 1)));
        })
      );
    }

    // Unexpected response format
    return throwError(() => new Error("Unexpected response format from ToC API"));
  }

  /**
   * Handles HTTP errors
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    this.loadingSubject.next(false);

    let errorMessage = "An error occurred while fetching the table of contents";

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      switch (error.status) {
        case 401:
          errorMessage = "You must be logged in to view the table of contents";
          break;
        case 403:
          errorMessage = "You do not have permission to view this table of contents";
          break;
        case 404:
          errorMessage = "Topic not found";
          break;
        case 500:
          errorMessage = "Server error occurred while generating table of contents";
          break;
        default:
          errorMessage = `Error ${error.status}: ${error.message}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }

  /**
   * Manually triggers a rebuild of the ToC (for future enhancement)
   * @param topicId The ID of the forum topic
   * @returns Observable indicating rebuild was initiated
   */
  rebuildTableOfContents(topicId: number): Observable<void> {
    const url = `${this.tocUrl}/${topicId}/toc/rebuild/`;

    return this.http.post<void>(url, {}).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * @deprecated Use getTableOfContents() which now includes all entry data
   * @param topicId The ID of the forum topic
   * @returns Observable of ToC entries array
   */
  getTocEntries(topicId: number): Observable<ForumTocEntry[]> {
    console.warn("getTocEntries is deprecated. Use getTableOfContents() which now includes all entry data.");
    // For backward compatibility, extract entries from the unified response
    return this.getTableOfContents(topicId).pipe(map(tocData => tocData.results || []));
  }

  /**
   * Edits a ToC entry summary (v2 API)
   * @param topicId The ID of the forum topic
   * @param entryId The ID of the ToC entry
   * @param summary The new summary text
   * @param editReason Optional reason for the edit
   * @returns Observable of the updated ToC entry
   */
  editTocEntry(topicId: number, entryId: number, summary: string, editReason?: string): Observable<ForumTocEntry> {
    const url = `${this.baseUrl}/forum/topic/${topicId}/toc/entries/${entryId}/edit/`;
    const body: ForumTocEditRequest = { summary };

    if (editReason) {
      body.editReason = editReason;
    }

    this.loadingSubject.next(true);

    return this.http.patch<ForumTocEntry>(url, body).pipe(
      map(response => {
        this.loadingSubject.next(false);
        return response;
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetches the edit history for a ToC entry (v2 API)
   * @deprecated This endpoint is deprecated. Use getTocEntries() which includes embedded history.
   * @param topicId The ID of the forum topic
   * @param entryId The ID of the ToC entry
   * @returns Observable of the edit history
   */
  getTocEntryHistory(topicId: number, entryId: number): Observable<ForumTocEditHistory[]> {
    const url = `${this.baseUrl}/forum/topic/${topicId}/toc/entries/${entryId}/history/`;

    return this.http.get<ForumTocEditHistory[]>(url).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Fetches the Table of Contents for a forum topic by URL
   * Used for pagination when loading next/previous pages
   * @param url The URL to fetch (from next/previous field)
   * @returns Observable of the ToC data
   */
  getTableOfContentsByUrl(url: string): Observable<ForumTocData> {
    this.loadingSubject.next(true);

    return this.http.get<ForumTocResponse>(url).pipe(
      catchError(error => this.handleError(error)),
      map(response => {
        this.loadingSubject.next(false);
        // Since pagination doesn't require building state, we can directly return the response
        return response as ForumTocData;
      })
    );
  }

  /**
   * Fetches a specific page of the Table of Contents for a forum topic
   * @param topicId The ID of the forum topic
   * @param page The page number to fetch
   * @param pageSize The number of entries per page
   * @returns Observable of the ToC data
   */
  getTableOfContentsPage(topicId: number, page: number, pageSize: number): Observable<ForumTocData> {
    const url = `${this.tocUrl}/${topicId}/toc/`;
    const params = {
      page: page.toString(),
      page_size: pageSize.toString()
    };

    this.loadingSubject.next(true);

    return this.http.get<ForumTocResponse>(url, { params }).pipe(
      catchError(error => this.handleError(error)),
      map(response => {
        this.loadingSubject.next(false);
        // Since pagination doesn't require building state, we can directly return the response
        return response as ForumTocData;
      })
    );
  }
}
